import { useTranslation } from 'react-i18next'
import { formatPrice } from '../../lib/utils'

interface PriceDisplayProps {
  effective_price: string | null
  effective_price_inc_vat: string | null
  /** Server-formatted inc-VAT price (Money VO, T1.1). Used verbatim when present. */
  price_formatted?: string | null
  large?: boolean
  /** Where "offerte aanvragen" links to; defaults to the quote page. */
  quoteHref?: string
}

export default function PriceDisplay({
  effective_price,
  effective_price_inc_vat,
  price_formatted,
  large,
  quoteHref = '/nl/product-quote',
}: PriceDisplayProps) {
  const { t } = useTranslation()
  const price = effective_price ? parseFloat(effective_price) : null
  const priceIncVat = effective_price_inc_vat ? parseFloat(effective_price_inc_vat) : null

  // T1.3 / B2 / G13: no price in our sources (RS + Excel) → never fabricate one,
  // route the buyer to a quote instead of showing a made-up or "0" price.
  if (price == null) {
    return (
      <div className="flex flex-col gap-0.5">
        <span className={large ? 'text-base text-neutral-600 italic' : 'text-sm text-neutral-600 italic'}>
          {t('product.priceOnRequest')}
        </span>
        <a
          href={quoteHref}
          className={`${large ? 'text-sm' : 'text-xs'} font-medium text-primary hover:text-primary-hover underline underline-offset-2`}
        >
          {t('product.requestQuote')}
        </a>
      </div>
    )
  }

  const exVatClass = large ? 'text-2xl font-bold text-neutral-900' : 'text-sm font-semibold text-neutral-900'
  const labelClass = large ? 'text-xs text-neutral-600 mt-0.5' : 'text-xs text-neutral-600'
  const incVatClass = large ? 'text-sm text-neutral-600' : 'text-xs text-neutral-600'

  // Prefer the server-formatted inc-VAT price (single source of truth, T1.1);
  // fall back to client formatting when the field is absent (older API).
  const incVatText = price_formatted ?? (priceIncVat != null ? formatPrice(priceIncVat, 'EUR') : null)

  return (
    <div className="flex flex-col">
      <span className={exVatClass}>{formatPrice(price, 'EUR')}</span>
      <span className={labelClass}>{t('product.exclVat')}</span>
      {incVatText != null && (
        <span className={incVatClass}>{t('product.inclVat')} {incVatText}</span>
      )}
    </div>
  )
}
